import type { Page } from '../../types';
import { iybCriteria, rewardVoucherCriteria } from './data';

const rewardDetails: Page[] = [
  {
    pageType: 'question',
    title: 'Reward details',
    name: 'reward-details',
    description:
      'Choose the type of award first. Reward vouchers must be between &#163;10 and &#163;200 and in increments of &#163;5. In-year bonuses are limited to &#163;500, &#163;750 or &#163;1000. For team nominations, the amount should be the total amount for the team. ' +
      'Further Guidance on submitting a nomination is available on the <a target=_blank href=https://sites.google.com/cabinetoffice.gov.uk/digital-staff-pages/reward-and-recognition>CO Digital to Community Hub</a>',
    questions: [
      {
        questionType: 'radio',
        title: 'Is this a reward voucher or an in-year bonus (IYB)?',
        name: 'reward-type',
        options: [
          {
            option: 'Reward voucher',
            questions: [
              {
                questionType: 'checkbox',
                title: 'Select 1 or more reward voucher criteria',
                name: 'reward-voucher-criteria',
                options: rewardVoucherCriteria,
                rules: {
                  required: true,
                },
              },
              {
                questionType: 'select',
                athenaType: 'decimal(18,2)',
                title: 'Voucher amount',
                name: 'reward-voucher-amount',
                hintOption: 'Select an amount',
                detailsHeading: 'Guidance on choosing a reward amount',
                detailsContent:
                  '<p>Thank you e-vouchers are held in denominations of \u00a35, and can be issued in multiple units from a minimum of \u00a310 up to a maximum of \u00a3200 at a time.</p>' +
                  '<p>When awarding a thank you e-voucher to a team (which may consist of 2 or more employees), there is a cap of \u00a3200 for the team.</p>' +
                  '<p><strong>\u00a310 - \u00a350</strong><br />It is likely that the employee or team has met at least one of the criteria for nomination and completed work, and demonstrated the Cabinet Office Values in a way which deserves recognition. It is likely that the employee\'s contribution has positively impacted their immediate team.</p>' +
                  '<p>Examples: organising a team event, supporting new members of the team or stepping in to help support a colleague at short notice to achieve a business objective, demonstrating clear collaboration.</p>' +
                  '<p><strong>\u00a360 - \u00a3100</strong><br />It is likely that the employee or team has met more than one of the criteria for nomination and completed work, and demonstrated the Cabinet Office Values to a particularly high standard. It is likely that they have impacted upon their wider business area.</p>' +
                  '<p>Example: putting forward cost-saving ideas to SMT which result in positive outcomes or proposing refreshed ways of working to promote trust and empowerment of teams, ensuring that team objectives are delivered efficiently.</p>' +
                  '<p><strong>\u00a3110 - \u00a3200</strong><br />It is likely that the employee or team has met multiple criteria for nomination. They will have demonstrated Cabinet Office values and their actions will have had a positive impact on their business area, or the wider Department.</p>' +
                  '<p>Example: contributing to a project conducted by an employee network group, helping to make Cabinet Office a more respectful organisation which promotes inclusion.</p>',
                options: [
                  { option: '\u00a310', value: '10.00' },
                  { option: '\u00a325', value: '25.00' },
                  { option: '\u00a350', value: '50.00' },
                  { option: '\u00a375', value: '75.00' },
                  { option: '\u00a3100', value: '100.00' },
                  { option: '\u00a3150', value: '150.00' },
                  { option: '\u00a3200', value: '200.00' },
                  {
                    option: 'Other',
                    questions: [
                      {
                        questionType: 'textInput',
                        athenaType: 'decimal(18,2)',
                        title: 'Enter voucher amount in pounds',
                        name: 'reward-voucher-other-amount',
                        rules: {
                          required: true,
                          decimal: 'decimal(2)',
                          min: 10,
                          max: 200,
                        },
                        errorMessages: {
                          min: 'Voucher amount must be \u00a310 or more',
                          max: 'Voucher amount must be \u00a3200 or less',
                        },
                      },
                    ],
                  },
                ],
                rules: {
                  required: true,
                },
              },
            ],
          },
          {
            option: 'In-year bonus (IYB)',
            questions: [
              {
                questionType: 'checkbox',
                title: 'Select 1 or more in-year bonus criteria',
                name: 'iyb-criteria',
                options: iybCriteria,
                rules: {
                  required: true,
                },
              },
              {
                questionType: 'select',
                athenaType: 'decimal(18,2)',
                title: 'In-year bonus amount',
                name: 'iyb-amount',
                hintOption: 'Select an amount',
                detailsHeading: 'Guidance on choosing a reward amount',
                detailsContent:
                  '<p>When awarding an in-year bonus to a team, the same three levels apply to the team.</p>' +
                  '<p>As the team has met the associated criteria for the award, as opposed to a single employee, this value will be split equally between the members of the team being nominated.</p>' +
                  '<p>For example, if a team of 4 is awarded a \u00a31000 in-year bonus, each member of the team will receive \u00a3250 for their contribution.</p>' +
                  '<p><strong>\u00a3500</strong><br />It is likely that the employee or team has met a number of the criteria for nomination. They will exceed expectations and go beyond their role or team requirements, demonstrating the Cabinet Office Values and having a significant impact on the achievement of team objectives on a long-term basis.</p>' +
                  '<p>Examples: delivering a demanding set of tasks to high quality, effective contribution to the delivery of a specific change, initiative or project or proactively setting up a working group to ensure that a wide range of perspectives are taken into account in decision-making.</p>' +
                  '<p><strong>\u00a3750</strong><br />It is likely that the employee or team has met most of the criteria for nomination and demonstrated the Cabinet Office Values. Their work or actions will have proved invaluable to the organisation or team and will have a long-term impact. They will have completed the work or action to an outstanding standard.</p>' +
                  '<p>Examples: dealing with multiple challenging stakeholders simultaneously and over a sustained period of time, building strong relationships and enhancing the team or Cabinet Office\'s reputation and enabling the business to meet a key priority, or a team working together to achieve a critical task.</p>' +
                  '<p><strong>\u00a31,000</strong><br />It is likely that the employee or team meets all of the criteria for nomination, including demonstration of the Cabinet Office Values. They have performed actions which have a significant and long-term impact on the achievement of Cabinet Office\'s objectives and the enhancement of the Department\'s reputation.</p>' +
                  '<p>Examples: leading a cross-unit/group/departmental/government review e.g. a capability review or restructure, or taking a lead role in setting up and running a new network for Cabinet Office staff.</p>',
                options: [
                  { option: '\u00a3500', value: '500.00' },
                  { option: '\u00a3750', value: '750.00' },
                  { option: '\u00a31000', value: '1000.00' },
                ],
                rules: {
                  required: true,
                },
              },
            ],
          },
        ],
        rules: {
          required: true,
        },
      },
      {
        questionType: 'textarea',
        title: 'Please give a detailed description of the reason for the nomination',
        name: 'nomination-justification-text',
        hint:
          'Use these headers to keep your writing simple and clear.\n\n\n' +
          'S - Situation: What was the context? ("During a period of high pressure...")  \n' +
          'T - Task: What was their role? ("They were responsible for the daily tracker...")  \n' +
          'A - Action: What did they do that was exceptional? ("They identified a data flaw and fixed it for the whole team, saving us 3 hours of work a week.")  \n' +
          'R - Result: What was the impact? ("This ensured the Director had perfect data and protected our reputation for accuracy.")  \n' +
          'V - Value: Name the CO Value. ("They showed Trust by taking personal accountability for this task.")\n\n\n' +
          '**This justification will be shared with the nominee and their line manager if the nomination is successful and may be edited by the panel.**',
        textRows: 12,
        wordCount: 500,
        rules: {
          required: true,
          pattern: '^(?:\\S+\\s+){49,}\\S+(?:\\s.*)?$',
        },
        errorMessages: {
          required: 'Your nomination must contain at least 50 words.',
          pattern: 'Your nomination must contain at least 50 words.',
        },
      },
    ],
  },
];

export default rewardDetails;
